import React, { useEffect, useState } from 'react';
import { Card, CardBody, CardHeader, Table } from 'reactstrap';
import moment from 'moment/moment';
import { axiosGet } from '../../util/apiConfig';
import { accountLabel } from '../../util/constants';

const formatDate = (unix) => (unix ? moment.unix(unix).format('YYYY-MM-DD') : '');
const formatAmount = (amount, currency) => {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: (currency || 'usd').toUpperCase() }).format(amount / 100);
  } catch (e) {
    return `${(amount / 100).toFixed(2)} ${(currency || '').toUpperCase()}`;
  }
};

const statusCell = (item) => {
  if (item.status === 'PAID') return <span className="text-success">Paid {formatDate(item.paidAt)}</span>;
  if (item.status === 'LIKELY_PAID') return <span className="text-success">Likely paid {formatDate(item.paidAt)}</span>;
  if (item.status === 'WRITTEN_OFF') return <span className="text-muted">Written off</span>;
  return <strong className="text-danger">Unpaid</strong>;
};

// Every failed charge in the last 6 months across the 4 Stripe accounts, with whether it was paid later.
export default function FailedPayments() {
  const [attempt, setAttempt] = useState(0);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    setReport(null);
    axiosGet('payment/failed-payments-report')
      .then((response) => {
        if (!active) return;
        if (response.isError || !response.data || !Array.isArray(response.data.items)) setError(true);
        else setReport(response.data);
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setError(true);
        setLoading(false);
      });
    return () => { active = false; };
  }, [attempt]);

  // Stripe retries an invoice several times; each retry is a failed attempt but one unpaid bill.
  const unpaid = report
    ? new Set(report.items.filter((item) => item.status === 'UNPAID').map((item) => item.invoice || item.chargeId)).size
    : 0;

  return (
    <Card className="shadow border-0" style={{ borderRadius: '12px', overflow: 'hidden' }}>
      <CardHeader className="border-0 bg-transparent py-4">
        <h3 className="mb-0" style={{ fontWeight: '700', color: '#32325d' }}>
          <i className="fa fa-exclamation-circle mr-2 text-danger" style={{ fontSize: '1.2rem' }}></i> Failed Payments
        </h3>
        {report && <p className="text-muted small mb-0 mt-2">
          {unpaid} unpaid · {report.items.length} failed attempts in the last 6 months (Chess, Code, Maths, ELA Club) · as of {moment(report.generatedAt).format('YYYY-MM-DD HH:mm')}
        </p>}
      </CardHeader>
      <CardBody aria-busy={loading}>
        {loading && <p role="status">Loading failed payments…</p>}
        {error && <div role="alert">
          <p>Could not load failed payments.</p>
          <button type="button" className="btn btn-sm btn-primary" onClick={() => setAttempt((value) => value + 1)}>Retry</button>
        </div>}
        {report && <>
          {report.errors.length > 0 && <div role="status" className="alert alert-warning small">
            Could not load {report.errors.map((e) => accountLabel(e.account)).join(', ')} from Stripe; those payments are missing. <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={() => setAttempt((value) => value + 1)}>Retry</button>
          </div>}
          {report.items.length === 0 ? <p>{report.errors.length ? 'No failed payments in the accounts that loaded.' : 'No failed payments in the last 6 months.'}</p> : (
            <div style={{ maxHeight: '500px', overflow: 'auto' }}>
              <Table className="align-items-center table-flush" responsive size="sm">
                <thead className="thead-light"><tr>
                  <th scope="col">Name</th>
                  <th scope="col">Email</th>
                  <th scope="col">Category</th>
                  <th scope="col">Date</th>
                  <th scope="col">Amount</th>
                  <th scope="col">Reason</th>
                  <th scope="col">Status</th>
                </tr></thead>
                <tbody>{report.items.map((item) => <tr key={`${item.account}|${item.chargeId}`}>
                  <td>{item.name}</td>
                  <td>{item.email}</td>
                  <td>{accountLabel(item.account)}</td>
                  <td>{formatDate(item.created)}</td>
                  <td>{formatAmount(item.amount, item.currency)}</td>
                  <td style={{ whiteSpace: 'normal', minWidth: '160px' }}>{item.reason}</td>
                  <td>{statusCell(item)}</td>
                </tr>)}</tbody>
              </Table>
            </div>
          )}
        </>}
      </CardBody>
    </Card>
  );
}
